import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const showcases = [
  {
    id: "salon-marlene",
    client: "Salon Marlene",
    category: "Friseursalon",
    year: "2026",
    before: "/images/showcases/salon-before.webp",
    after: "/images/showcases/salon-after.webp",
    result: "Von Web-1.0-Chaos zu elegantem Auftritt mit Online-Terminbuchung.",
    href: "/demo/salon-marlene",
  },
  {
    id: "schreinerei-brenner",
    client: "Schreinerei Brenner",
    category: "Handwerksbetrieb",
    year: "2026",
    before: "/images/showcases/handwerk-before.webp",
    after: "/images/showcases/handwerk-after.webp",
    result: "Handwerkskunst endlich sichtbar: Projektgalerie statt Baustellen-Banner.",
    href: "/demo/schreinerei-brenner",
  },
  {
    id: "physio-sommer",
    client: "Physiotherapie Sommer",
    category: "Praxis & Gesundheit",
    year: "2026",
    before: "/images/showcases/physio-before.webp",
    after: "/images/showcases/physio-after.webp",
    result: "Von der Textwüste zur einladenden Praxis-Website mit Online-Termin.",
    href: "/demo/physiotherapie-sommer",
  },
];

export default function Showcase() {
  return (
    <section
      id="showcase"
      data-testid="showcase-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold">
              Vorher / Nachher
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-zinc-900 mt-4">
              Sichtbarer Unterschied.
            </h2>
          </div>
          <p className="max-w-sm text-zinc-500">
            So sieht der NovaSite-Glow-Up aus: links die veraltete Website, rechts der moderne
            Auftritt, der Kunden überzeugt.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {showcases.map((s, i) => {
            const Wrapper = s.href ? Link : "div";
            const wrapperProps = s.href ? { to: s.href } : {};
            return (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`showcase-${s.id}`}
              className="group bg-white border border-zinc-100 rounded-3xl overflow-hidden hover:shadow-[0_16px_48px_rgba(0,0,0,0.06)] hover:-translate-y-1 transition-all duration-300"
            >
              <Wrapper {...wrapperProps} className="block">
              <div className="relative grid grid-cols-2 aspect-[4/3] bg-zinc-50">
                <div className="relative border-r border-zinc-100 overflow-hidden">
                  <img
                    src={s.before}
                    alt={`${s.client} — Website vorher (veraltetes Design)`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-top grayscale-[0.35] group-hover:grayscale-0 transition-all duration-500"
                  />
                  <div className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-widest bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-zinc-700">
                    Vorher
                  </div>
                </div>
                <div className="relative overflow-hidden">
                  <img
                    src={s.after}
                    alt={`${s.client} — Website nachher (modernes NovaSite Redesign)`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-widest bg-emerald-500 text-white px-2.5 py-1 rounded-full">
                    {s.href ? "Live-Demo" : "Nachher"}
                  </div>
                </div>
              </div>

              <div className="p-6 md:p-7 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-zinc-400">{s.category}</div>
                  <h3 className="font-display text-xl font-medium text-zinc-900 mt-1.5">
                    {s.client}
                  </h3>
                  <p className="text-sm text-zinc-500 mt-2">{s.result}</p>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-xs text-zinc-400 mb-2">{s.year}</div>
                  <ArrowUpRight size={18} className="text-zinc-400 group-hover:text-zinc-900 group-hover:rotate-45 transition-all" />
                </div>
              </div>
              </Wrapper>
            </motion.div>
          );})}
        </div>

        <p className="text-center mt-10 text-sm text-zinc-500">
          Dein Projekt könnte das nächste sein.{" "}
          <Link to="/kontakt" className="text-zinc-900 underline underline-offset-4">
            Lass uns über Deine Website sprechen
          </Link>
          .
        </p>
        <p data-testid="showcase-disclosure" className="text-center mt-6 text-[10px] leading-relaxed text-zinc-300">
          Dargestellte Projekte sind Design-Konzepte zur Veranschaulichung der Arbeitsweise.
        </p>
      </div>
    </section>
  );
}
